import {CommonModule} from '@angular/common';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {RouterModule} from '@angular/router';
import {of} from 'rxjs';
import {catchError, finalize} from 'rxjs/operators';
import {MaterialModule} from '../../../material.module';
import {
    ConnectConnectionsService,
    ConnectOAuthAuthorizationRequest,
    ConnectServerConnection
} from '../../../services/connect/connect-connections.service';
import {ClientService} from '../../../services/auth/client.service';
import {ConnectOAuthPopupService} from '../../../services/connect/connect-oauth-popup.service';
import {
    ConnectAuthType,
    ConnectProviderDef,
    ConnectProvidersService
} from '../../../services/connect/connect-providers.service';
import {formatHttpErrorMessage} from '../../../services/connect/http-error-message';
import {ToastService} from '../../../services/layout/toast.service';
import {ProductType} from '../../../models/client-options.model';
import {normalizeProductType} from '../../../shared/products';

type Policy = {name: string; detail: string; enabled: boolean};
type AuthType = 'oauth2' | 'api_key' | 'bearer' | 'basic' | 'custom';
type BadgeLabel = 'Connected' | 'Disconnected' | 'Expiring soon' | 'Expired';
type BadgeResult = {label: BadgeLabel; className: string};
type EditState = {form: FormGroup; expanded: boolean};

@Component({
  selector: 'app-connect-connections',
  standalone: true,
  imports: [CommonModule, RouterModule, ReactiveFormsModule, MaterialModule],
  templateUrl: './connect-connections.component.html',
  styleUrls: ['../connect-shared.scss', './connect-connections.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {class: 'connect-theme'},
})
export class ConnectConnectionsComponent implements OnInit {
  private readonly connectionsService = inject(ConnectConnectionsService);
  private readonly providersService = inject(ConnectProvidersService);
    private readonly clientService = inject(ClientService);
    private readonly oauthPopup = inject(ConnectOAuthPopupService);
  private readonly toastService = inject(ToastService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);

  connections: ConnectServerConnection[] = [];
  providers: ConnectProviderDef[] = [];
  policies: Policy[] = [
    {name: 'Redact-then-route', detail: 'Mask PII before storage and routing.', enabled: true},
    {name: 'Scale to zero', detail: 'Sleep workers with zero traffic.', enabled: true},
    {name: 'Audit log', detail: 'Persist workflow + auth events.', enabled: true},
  ];

  loadingConnections = false;
  loadingProviders = false;
  error: string | null = null;
  showCreate = false;
  createBusy = false;
  savingIds = new Set<string>();
  authenticatingId: string | null = null;
  private editing = new Map<string, EditState>();

  readonly createForm = this.fb.group({
    name: ['', Validators.required],
    providerId: [''],
      ref: ['custom.oauth2', Validators.required],
    region: ['eu'],
    authType: this.fb.control<AuthType>('oauth2', {nonNullable: true}),
    apiKeyName: [''],
    apiKeyValue: [''],
    apiKeyIn: ['header'],
    apiKeyPrefix: [''],
    bearerToken: [''],
    bearerPrefix: ['Bearer'],
    basicUsername: [''],
    basicPassword: [''],
    customHost: [''],
    customPort: [''],
    customUsername: [''],
    customPassword: [''],
    customPrivateKey: [''],
    oauthRefreshToken: [''],
    oauthAccessToken: [''],
    oauthExpiresAt: [''],
  });

  ngOnInit(): void {
    this.loadConnections();
    this.loadProviders();
  }

  get connectedCount(): number {
    return this.connections.filter(c => this.badgeFor(c).label === 'Connected').length;
  }

  get expiringCount(): number {
    return this.connections.filter(c => this.badgeFor(c).label === 'Expiring soon').length;
  }

  loadConnections(): void {
    this.loadingConnections = true;
    this.connectionsService
      .list()
      .pipe(
        catchError(err => {
          const msg = formatHttpErrorMessage(err, 'Could not load connections yet.');
          console.error('Failed to load connections', err);
          this.error = msg;
          this.toastService.showError(msg);
          this.connections = [];
          this.cdr.markForCheck();
          return of<ConnectServerConnection[]>([]);
        }),
        finalize(() => {
          this.loadingConnections = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe(connections => {
        this.connections = connections;
        this.editing.clear();
        this.cdr.markForCheck();
      });
  }

  private loadProviders(): void {
    this.loadingProviders = true;
    this.providersService
      .fetchCatalog()
      .pipe(
        catchError(err => {
          const msg = formatHttpErrorMessage(err, 'Could not load provider catalog.');
          console.error('Failed to load providers', err);
          this.error = this.error ?? msg;
          this.toastService.showError(msg);
          this.providers = [];
          this.cdr.markForCheck();
          return of({version: 'v1', providers: []} as const);
        }),
        finalize(() => {
          this.loadingProviders = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe(catalog => {
        this.providers = [...(catalog.providers || [])];
        this.cdr.markForCheck();
      });
  }

  toggleCreate(): void {
    this.showCreate = !this.showCreate;
    this.cdr.markForCheck();
  }

  providerDocsUrl(providerId: string | null | undefined): string {
    if (!providerId) return '#';
    const provider = this.providers.find(p => p.id === providerId);
    return provider?.docsUrl || '#';
  }

    providerLabel(connection: ConnectServerConnection): string {
        const ref = (connection.ref || '').trim();
        const provider = ref ? this.providers.find(p => p.ref === ref) : undefined;
        if (provider?.name) return provider.name;
        if (!ref || ref.startsWith('custom.')) return 'Custom';
        return 'Unknown provider';
    }

    typeLabel(type: unknown): string {
        const raw = typeof type === 'string' ? type : '';
        const normalized = raw.trim().toLowerCase();
        switch (normalized) {
            case 'oauth2':
            case 'oauth':
                return 'OAuth';
            case 'api_key':
            case 'api-key':
            case 'apikey':
                return 'API key';
            case 'bearer':
                return 'Bearer';
            case 'basic':
                return 'Basic auth';
            case 'custom':
            case 'sftp':
                return 'Custom';
            default:
                return normalized ? raw : 'OAuth';
        }
    }

  authOptions(providerId?: string): {value: AuthType; label: string}[] {
    const allOptions: {value: AuthType; label: string}[] = [
      {value: 'oauth2', label: 'OAuth 2.0'},
      {value: 'api_key', label: 'API key'},
      {value: 'bearer', label: 'Bearer token'},
      {value: 'basic', label: 'Basic auth'},
      {value: 'custom', label: 'Custom'},
    ];
    if (!providerId) return allOptions;
    const provider = this.providers.find(p => p.id === providerId);
    if (!provider) return allOptions;
    const supported = new Set<string>((provider.auth?.supported || []).map(s => String(s)));
    const map: Record<string, AuthType> = {oauth: 'oauth2', apiKey: 'api_key', bearer: 'bearer', basic: 'basic', sftp: 'custom'};
    const allowed = allOptions.filter(opt => {
      if (opt.value === 'custom') return true;
      const matches = Object.entries(map)
        .filter(([, ui]) => ui === opt.value)
        .map(([key]) => key);
      return matches.some(key => supported.has(key));
    });
    return allowed.length ? allowed : allOptions;
  }

  onProviderChanged(providerId: string): void {
    const provider = this.providers.find(p => p.id === providerId);
      if (!provider) {
          const currentType = (this.createForm.value.authType || 'oauth2') as AuthType;
          const currentRef = (this.createForm.value.ref || '').trim();
          const isCatalogRef = currentRef ? this.providers.some(p => p.ref === currentRef) : false;
          if (!currentRef || currentRef.startsWith('custom.') || isCatalogRef) {
              this.createForm.patchValue({ref: `custom.${currentType}`});
          }
          this.cdr.markForCheck();
          return;
      }
      const mapped = this.mapProviderAuthType(provider.auth?.default);
      const nextType = (mapped || this.createForm.value.authType || 'oauth2') as AuthType;
      this.createForm.patchValue({ref: provider.ref || '', authType: nextType});
      this.cdr.markForCheck();
  }

    onEditProviderChanged(connectionId: string, providerId: string): void {
        const state = this.editing.get(connectionId);
        if (!state) return;

        const provider = this.providers.find(p => p.id === providerId);
        if (!provider) {
            const currentType = ((state.form.get('authType')?.value as AuthType | null | undefined) || 'oauth2') as AuthType;
            const currentRef = String(state.form.get('ref')?.value ?? '').trim();
            const isCatalogRef = currentRef ? this.providers.some(p => p.ref === currentRef) : false;
            if (!currentRef || currentRef.startsWith('custom.') || isCatalogRef) {
                state.form.patchValue({ref: `custom.${currentType}`});
            }
            this.cdr.markForCheck();
            return;
        }

        const mapped = this.mapProviderAuthType(provider.auth?.default);
        const nextType = (mapped || (state.form.get('authType')?.value as AuthType | null | undefined) || 'oauth2') as AuthType;
        state.form.patchValue({
            ref: provider.ref || '',
            authType: nextType,
    });
    this.cdr.markForCheck();
  }

  setCreateAuthType(type: AuthType): void {
    this.createForm.patchValue({authType: type});
      if (!this.createForm.value.providerId) {
          const currentRef = (this.createForm.value.ref || '').trim();
          if (!currentRef || currentRef.startsWith('custom.')) {
              this.createForm.patchValue({ref: `custom.${type}`});
          }
    }
    this.cdr.markForCheck();
  }

  createConnection(): void {
    if (this.createForm.invalid) {
      this.createForm.markAllAsTouched();
      return;
    }
    const value = this.createForm.getRawValue();
    const ref = (value.ref || '').trim() || this.providerRefFor(value.providerId) || `custom.${value.authType}`;
    const payload = {
      name: value.name?.trim() || '',
      ref,
      provider_ref: ref,
      config: this.buildConfig(value),
    };
    this.createBusy = true;
    this.connectionsService
      .create(payload)
      .pipe(
        catchError(err => {
          const msg = formatHttpErrorMessage(err, 'Could not create connection.');
          console.error('Failed to create connection', err);
          this.error = msg;
          this.toastService.showError(msg);
          this.cdr.markForCheck();
          return of(null);
        }),
        finalize(() => {
          this.createBusy = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe(conn => {
        if (!conn) return;
        this.connections = [conn as ConnectServerConnection, ...this.connections];
        this.resetCreateForm();
        this.showCreate = false;
        this.toastService.showSuccess('Connection created', 2500);
        this.cdr.markForCheck();
      });
  }

  toggleEdit(connection: ConnectServerConnection): void {
    const existing = this.editing.get(connection.id);
    if (existing) {
      existing.expanded = !existing.expanded;
      this.editing.set(connection.id, {...existing});
    } else {
      this.editing.set(connection.id, {form: this.buildEditForm(connection), expanded: true});
    }
    this.cdr.markForCheck();
  }

  editForm(id: string): FormGroup | null {
    return this.editing.get(id)?.form || null;
  }

  isEditOpen(id: string): boolean {
    return this.editing.get(id)?.expanded ?? false;
  }

  setEditAuthType(id: string, type: AuthType): void {
    const state = this.editing.get(id);
    if (!state) return;
    state.form.patchValue({authType: type});
      if (!state.form.value['providerId']) {
          const currentRef = String(state.form.get('ref')?.value ?? '').trim();
          if (!currentRef || currentRef.startsWith('custom.')) {
              state.form.patchValue({ref: `custom.${type}`});
          }
    }
    this.cdr.markForCheck();
  }

  saveEdit(connection: ConnectServerConnection): void {
    const state = this.editing.get(connection.id);
    if (!state) return;
    if (state.form.invalid) {
      state.form.markAllAsTouched();
      return;
    }
    const value = state.form.getRawValue();
    const ref = (value.ref || '').trim() || connection.ref;
    const payload = {
      name: value.name?.trim() || connection.name,
      ref,
      provider_ref: ref,
      config: this.buildConfig(value),
    };
    this.savingIds.add(connection.id);
    this.connectionsService
      .update(connection.id, payload)
      .pipe(
        catchError(err => {
          const msg = formatHttpErrorMessage(err, 'Could not save connection.');
          console.error('Failed to save connection', err);
          this.error = this.error ?? msg;
          this.toastService.showError(msg);
          this.cdr.markForCheck();
          return of(null);
        }),
        finalize(() => {
          this.savingIds.delete(connection.id);
          this.cdr.markForCheck();
        }),
      )
      .subscribe(updated => {
        if (!updated) return;
        this.connections = this.connections.map(conn => (conn.id === connection.id ? (updated as ConnectServerConnection) : conn));
        this.editing.set(connection.id, {form: this.buildEditForm(updated as ConnectServerConnection), expanded: true});
        this.toastService.showSuccess('Connection saved', 2500);
        this.cdr.markForCheck();
      });
  }

  delete(id: string): void {
    if (!id) return;
    const confirmed = window.confirm('Delete this connection?');
    if (!confirmed) return;
    this.savingIds.add(id);
    this.connectionsService
      .delete(id)
      .pipe(
        catchError(err => {
          const msg = formatHttpErrorMessage(err, 'Could not delete connection.');
          console.error('Failed to delete connection', err);
          this.error = this.error ?? msg;
          this.toastService.showError(msg);
          this.cdr.markForCheck();
          return of(null);
        }),
        finalize(() => {
          this.savingIds.delete(id);
          this.cdr.markForCheck();
        }),
      )
      .subscribe(() => {
        this.connections = this.connections.filter(c => c.id !== id);
        this.editing.delete(id);
        this.toastService.showSuccess('Connection deleted', 2500);
        this.cdr.markForCheck();
      });
  }

  badgeFor(connection: ConnectServerConnection): BadgeResult {
    const {expiresAt, connectedHint} = this.deriveBadgeInput(connection);
    return this.computeBadge({expiresAt, connectedHint});
  }

  isSaving(id: string): boolean {
    return this.savingIds.has(id);
  }

  maskSecret(value: unknown): string {
    const v = typeof value === 'string' ? value : '';
    if (!v) return '';
    const visible = Math.min(6, v.length);
    const maskedLen = Math.max(0, v.length - visible);
    return v.slice(0, visible) + (maskedLen > 0 ? '*'.repeat(maskedLen) : '');
  }

  async authenticateNew(): Promise<void> {
    const value = this.createForm.getRawValue();
    const providerRef = (value.ref || '').trim() || this.providerRefFor(value.providerId) || `custom.${value.authType}`;
    await this.launchAuth(providerRef, value.name?.trim() || undefined);
  }

  async authenticateExisting(connection: ConnectServerConnection): Promise<void> {
    const form = this.editing.get(connection.id)?.form;
    const ref = (form?.value.ref || connection.ref || '').trim();
    await this.launchAuth(ref, connection.id);
  }

  private resetCreateForm(): void {
    this.createForm.reset({
      name: '',
      providerId: '',
        ref: 'custom.oauth2',
      region: 'eu',
      authType: 'oauth2',
      apiKeyName: '',
      apiKeyValue: '',
      apiKeyIn: 'header',
      apiKeyPrefix: '',
      bearerToken: '',
      bearerPrefix: 'Bearer',
      basicUsername: '',
      basicPassword: '',
      customHost: '',
      customPort: '',
      customUsername: '',
      customPassword: '',
      customPrivateKey: '',
      oauthRefreshToken: '',
      oauthAccessToken: '',
      oauthExpiresAt: '',
    });
  }

  private buildConfig(value: Record<string, any>): Record<string, any> | undefined {
    const raw = value as Record<string, any>;
    const type = (raw['authType'] || 'oauth2') as AuthType;
    const region = raw['region'] || 'eu';
    const cfg: any = {type, region};
    if (type === 'api_key') {
      cfg.api_key = {
        key: raw['apiKeyValue'] || '',
        in: raw['apiKeyIn'] || 'header',
        name: raw['apiKeyName'] || '',
        prefix: raw['apiKeyPrefix'] || '',
      };
    }
    if (type === 'bearer') {
      cfg.bearer = {token: raw['bearerToken'] || '', prefix: raw['bearerPrefix'] || 'Bearer'};
    }
    if (type === 'basic') {
      cfg.basic = {username: raw['basicUsername'] || '', password: raw['basicPassword'] || ''};
    }
    if (type === 'oauth2') {
      cfg.oauth2 = {
        refresh_token: raw['oauthRefreshToken'] || '',
        access_token: raw['oauthAccessToken'] || '',
        expires_at: raw['oauthExpiresAt'] || undefined,
      };
    }
    if (type === 'custom') {
      cfg.custom = {
        host: raw['customHost'] || '',
        port: raw['customPort'] || '',
        username: raw['customUsername'] || '',
        password: raw['customPassword'] || '',
        privateKey: raw['customPrivateKey'] || '',
      };
    }
    return cfg;
  }

  private buildEditForm(connection: ConnectServerConnection): FormGroup {
    const cfg = this.stripRefFields((connection.config as any) || {});
    const type = ((cfg?.type as AuthType) || 'oauth2') as AuthType;
    const region = cfg?.region || 'eu';
      const providerId = this.providers.find(p => p.ref === connection.ref)?.id || '';
    const form = this.fb.group({
      name: [connection.name, Validators.required],
        providerId: [providerId],
      ref: [connection.ref, Validators.required],
      region: [region],
      authType: this.fb.control<AuthType>(type, {nonNullable: true}),
      apiKeyName: [cfg?.api_key?.name || ''],
      apiKeyValue: [cfg?.api_key?.key || ''],
      apiKeyIn: [cfg?.api_key?.in || 'header'],
      apiKeyPrefix: [cfg?.api_key?.prefix || ''],
      bearerToken: [cfg?.bearer?.token || ''],
      bearerPrefix: [cfg?.bearer?.prefix || 'Bearer'],
      basicUsername: [cfg?.basic?.username || ''],
      basicPassword: [cfg?.basic?.password || ''],
      customHost: [cfg?.custom?.host || ''],
      customPort: [cfg?.custom?.port || ''],
      customUsername: [cfg?.custom?.username || ''],
      customPassword: [cfg?.custom?.password || ''],
      customPrivateKey: [cfg?.custom?.privateKey || ''],
      oauthRefreshToken: [cfg?.oauth2?.refresh_token || ''],
      oauthAccessToken: [cfg?.oauth2?.access_token || ''],
      oauthExpiresAt: [cfg?.oauth2?.expires_at || ''],
    });
    return form;
  }

  private deriveBadgeInput(it: ConnectServerConnection): {expiresAt: string | null; connectedHint?: boolean} {
    const cfg: any = it.config || {};
    const type = (cfg.type ?? '').toString().toLowerCase();
    const oauth: any = cfg.oauth2 || {};
    const serverFlag = ((it as any).oauth_connected ?? (it as any).connected) as boolean | undefined;
    const refreshExpiresAt = oauth.refresh_token_expires_at || oauth.refresh_expires_at || oauth.refreshTokenExpiresAt || null;
    const hasRefresh = Boolean(oauth.refresh_token || oauth.refresh_token_ref) || serverFlag === true;
    const expiresAt: string | null =
      refreshExpiresAt || (type === 'oauth2' && hasRefresh ? null : ((it as any).expires_at || oauth?.expires_at || null));
    const connectedHint = serverFlag ?? (hasRefresh ? true : undefined);
    return {expiresAt, connectedHint};
  }

  private computeBadge(opts: {expiresAt?: string | null; connectedHint?: boolean}): BadgeResult {
    const now = Date.now();
    const expMs = opts.expiresAt ? Date.parse(String(opts.expiresAt)) : NaN;
    if (!Number.isNaN(expMs)) {
      if (expMs <= now) return {label: 'Expired', className: 'badge red'};
      const daysLeft = (expMs - now) / (1000 * 60 * 60 * 24);
      if (daysLeft <= 14) return {label: 'Expiring soon', className: 'badge amber'};
      return {label: 'Connected', className: 'badge green'};
    }
    if (opts.connectedHint === true) return {label: 'Connected', className: 'badge green'};
    if (opts.connectedHint === false) return {label: 'Disconnected', className: 'badge gray'};
    return {label: 'Connected', className: 'badge gray'};
  }

  private mapProviderAuthType(type?: ConnectAuthType | null): AuthType | null {
    if (!type) return null;
    const map: Record<ConnectAuthType, AuthType> = {
      oauth: 'oauth2',
      apiKey: 'api_key',
      bearer: 'bearer',
      basic: 'basic',
      sftp: 'custom',
    };
    return map[type] || null;
  }

  private providerRefFor(providerId?: string | null): string | null {
    if (!providerId) return null;
    const provider = this.providers.find(p => p.id === providerId);
    return provider?.ref || null;
  }

  private isHubspotProvider(providerRef: string): boolean {
    const normalized = (providerRef || '').trim().toLowerCase();
    if (!normalized) return false;
    return normalized === 'hubspot.oauth' || normalized === 'hubspot.oauth2' || normalized.startsWith('hubspot.');
  }

  private resolveHubspotScopeSetsForClient(): string[] {
    const enabledProducts = this.clientService.client?.enabledProducts ?? [];
    const mapped = new Set<string>();

    for (const product of enabledProducts) {
      switch (normalizeProductType(product)) {
        case ProductType.ShineEnrich:
          mapped.add('enrich');
          break;
        case ProductType.ShineWebinars:
          mapped.add('webinars');
          break;
        case ProductType.ShineAI:
          mapped.add('ai');
          break;
        case ProductType.ShineTranslations:
          mapped.add('translations');
          break;
        case ProductType.ShineConnect:
        default:
          break;
      }
    }

    const ordered = ['enrich', 'webinars', 'ai', 'translations'];
    return ordered.filter(scopeSet => mapped.has(scopeSet));
  }

  private stripRefFields(obj: any): any {
    if (!obj || typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj.map(v => this.stripRefFields(v));
    const out: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (key.endsWith('Ref') || key.endsWith('_ref')) continue;
      out[key] = this.stripRefFields(value);
    }
    return out;
  }

  private async launchAuth(providerRef: string, connectionRef?: string): Promise<void> {
    if (!providerRef) return;
    try {
      this.authenticatingId = connectionRef || 'new';
      const clientId = (this.clientService.client?.id || '').trim();
      if (!clientId) throw new Error('Select a client before connecting an account');
      const request: ConnectOAuthAuthorizationRequest = {
        provider_ref: providerRef, client_id: clientId,
        ...(connectionRef ? {connection_ref: connectionRef} : {}),
      };
      if (this.isHubspotProvider(providerRef)) {
        const scopeSets = this.resolveHubspotScopeSetsForClient();
        if (!scopeSets.length) throw new Error('Enable a HubSpot service before connecting this account');
        request.scope_set = scopeSets.join(',');
        request.include_optional_scopes = true;
      }
      await this.oauthPopup.authorize(request);
      this.toastService.showSuccess('Authentication successful', 2500);
    } catch (e: any) {
      const msg = e?.message || 'Authentication failed';
      this.toastService.showError(msg, 5000);
    } finally {
      this.authenticatingId = null;
      this.cdr.markForCheck();
    }
  }

}
